"use client";

import type * as React from "react";
import { useCallback, useRef, useState } from "react";

import type { ButtonProps } from "@/app/_ui/components/core/Button";
import { Button } from "@/app/_ui/components/core/Button";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";

import { DialogActionButton } from "@/app/_ui/components/core/DialogActionButton";

const TRANSITION = { duration: 0.25, ease: "easeOut" as const };
const AUTO_CLOSE_DELAY_MS = 3000;

export interface FormDialogButtonProps {
  /** Button label that opens the dialog */
  buttonLabel: string;
  /** Dialog title */
  title: string;
  /** Dialog description below the title */
  description: string;
  /** Success screen title */
  successTitle: string;
  /** Success screen message */
  successMessage: string;
  /** Success screen button label */
  successButton: string;
  /** Render the form. Receives onSuccess and onCancel callbacks. */
  renderForm: (props: { onSuccess: () => void; onCancel: () => void }) => React.ReactNode;
  onTriggerClick?: () => void;
  buttonVariant?: ButtonProps["variant"];
  buttonSize?: ButtonProps["size"];
  contentClassName?: string;
}

export function FormDialogButton({
  buttonLabel,
  title,
  description,
  successTitle,
  successMessage,
  successButton,
  renderForm,
  onTriggerClick,
  buttonVariant = "primary",
  buttonSize = "lg",
  contentClassName = "tw-w-[36rem] tw-max-w-[calc(100%-2rem)] tw-rounded-2xl",
}: FormDialogButtonProps) {
  const [succeeded, setSucceeded] = useState(false);
  const closeDialogRef = useRef<(() => void) | null>(null);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (isOpen) {
      clearTimeout(autoCloseTimerRef.current ?? undefined);
      setSucceeded(false);
    }
  }, []);

  const handleSuccess = useCallback(() => {
    setSucceeded(true);
    autoCloseTimerRef.current = setTimeout(() => closeDialogRef.current?.(), AUTO_CLOSE_DELAY_MS);
  }, []);

  return (
    <DialogActionButton
      label={buttonLabel}
      buttonVariant={buttonVariant}
      buttonSize={buttonSize}
      contentClassName={contentClassName}
      closeButtonVariant='inside'
      closeOnBackdropClick={false}
      onTriggerClick={onTriggerClick}
      onOpenChange={handleOpenChange}
    >
      {({ closeDialog }) => {
        closeDialogRef.current = closeDialog;
        return (
          <div className='tw-max-h-[80vh] tw-overflow-y-auto tw-p-6 md:tw-p-8'>
            <AnimatePresence mode='wait'>
              {!succeeded ? (
                <m.div key='form' exit={{ opacity: 0, y: -8 }} transition={TRANSITION}>
                  <div className='tw-mb-6 tw-text-center'>
                    <h3 className='tw-type-3xl tw-p-1 tw-mb-2'>{title}</h3>
                    <p className='tw-type-xs tw-text-gray-500 tw-p-1'>{description}</p>
                  </div>
                  {renderForm({ onSuccess: handleSuccess, onCancel: closeDialog })}
                </m.div>
              ) : (
                <m.div
                  key='success'
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={TRANSITION}
                  className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20 tw-text-center'
                >
                  <h3 className='tw-type-3xl tw-mb-5'>{successTitle}</h3>
                  <p className='tw-type-sm tw-text-gray-500 tw-mb-8 tw-max-w-sm'>{successMessage}</p>
                  <Button variant='primary' size='lg' onClick={closeDialog}>
                    {successButton}
                  </Button>
                </m.div>
              )}
            </AnimatePresence>
          </div>
        );
      }}
    </DialogActionButton>
  );
}
